import { useState } from "react";
import { ExternalLink, FileText, Upload, Download, PenLine } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Btn, Field, inputCls } from "@/components/Primitives";
import { SummitFormActions, SummitFormHeader, SummitFormModal } from "@/components/forms/SummitForm";

const SIGNABLE = new Set(["waiver", "consent", "liability_waiver", "guardian_consent"]);

export default function ParticipantJourneyDocuments({ documents = [], participantDocuments = [], participantId, onRefresh }) {
  const [busy, setBusy] = useState("");
  const [signing, setSigning] = useState(null);
  const [signature, setSignature] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [expiry, setExpiry] = useState({});
  if (!documents.length && !participantDocuments.length) return null;
  const submitFile = async (doc, file) => {
    if (!file) return;
    setBusy(doc.id);
    try {
      const body = new FormData();
      body.append("file", file);
      body.append("document_id", doc.id);
      body.append("expiry_date", expiry[doc.id] || "");
      await api.post(`/my/participant/${participantId}/documents/file`, body);
      toast.success("Document submitted for review");
      await onRefresh?.();
    } catch (error) { toast.error(formatApiError(error, "Could not submit document.")); }
    finally { setBusy(""); }
  };
  const download = async (doc) => {
    setBusy(doc.id);
    try {
      const { data } = await api.get(`/documents/${doc.id}/file`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const link = document.createElement("a");
      link.href = url;
      link.download = doc.file_name || doc.name;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) { toast.error(formatApiError(error, "Could not download document.")); }
    finally { setBusy(""); }
  };
  const sign = async (event) => {
    event.preventDefault();
    if (!signature.trim() || !accepted) return;
    setBusy(signing.id);
    try {
      await api.post(`/documents/${signing.id}/sign`, { signature_name: signature.trim(), accepted: true });
      setSigning(null);
      toast.success("Consent recorded");
      await onRefresh?.();
    } catch (error) { toast.error(formatApiError(error, "Could not record consent.")); }
    finally { setBusy(""); }
  };
  return <section className="participant-documents-section" data-testid="participant-documents">
    <div className="participant-section-kicker">Documents</div>
    <h2 className="participant-section-title">Trip files and requirements</h2>
    <div className="participant-documents-list">
      {documents.map((doc) => <div key={doc.id} className="flex flex-wrap items-center justify-between gap-2">
        <span className="flex items-center gap-2"><FileText size={16} /> {doc.name}</span>
        {doc.file_name ? <Btn type="button" variant="secondary" onClick={() => download(doc)} disabled={!!busy}><Download size={16} /> Download</Btn>
          : doc.link && <a href={doc.link} target="_blank" rel="noreferrer" className="flex items-center gap-1"><ExternalLink size={14} /> Open</a>}
      </div>)}
      {participantDocuments.map((doc) => <div key={doc.id} className="flex flex-col gap-3 border-b border-slate-200 py-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="flex items-center gap-2"><FileText size={16} /> {doc.name}</span>
          <span className="text-xs capitalize">{String(doc.status).replace(/_/g, " ")}</span>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          {doc.file_name && <Btn type="button" variant="secondary" disabled={!!busy} onClick={() => download(doc)}><Download size={16} /> Download</Btn>}
          {SIGNABLE.has(doc.type) ? doc.status !== "approved" && <Btn type="button" variant="secondary" disabled={!!busy || !doc.consent_text} onClick={() => { setSigning(doc); setSignature(""); setAccepted(false); }}><PenLine size={16} /> Review and sign</Btn>
            : <><Field label="Expiry date"><input type="date" className={inputCls} value={expiry[doc.id] || ""} onChange={(event) => setExpiry({ ...expiry, [doc.id]: event.target.value })} /></Field>
              <label className="inline-flex min-h-10 cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm"><Upload size={16} /> {busy === doc.id ? "Submitting..." : doc.status === "rejected" ? "Resubmit file" : "Submit file"}
                <input aria-label={`Submit ${doc.name}`} type="file" className="sr-only" disabled={!!busy} accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx" onChange={(event) => { submitFile(doc, event.target.files?.[0]); event.target.value = ""; }} />
              </label></>}
        </div>
        {SIGNABLE.has(doc.type) && !doc.consent_text && doc.status !== "approved" && <p className="text-xs text-amber-700">Awaiting consent text from organizer</p>}
      </div>)}
    </div>
    {signing && <SummitFormModal onClose={() => !busy && setSigning(null)} label="Review consent" className="max-w-xl">
      <form className="summit-form summit-form--sheet" onSubmit={sign}>
        <SummitFormHeader title={signing.name} onClose={() => !busy && setSigning(null)} />
        <div className="summit-form__body space-y-4">
          <p className="max-h-72 overflow-auto whitespace-pre-wrap text-sm">{signing.consent_text}</p>
          <Field label="Full name" required><input className={inputCls} required value={signature} onChange={(event) => setSignature(event.target.value)} /></Field>
          <label className="flex items-start gap-2 text-sm"><input type="checkbox" required checked={accepted} onChange={(event) => setAccepted(event.target.checked)} /> I agree to this consent statement.</label>
        </div>
        <SummitFormActions><Btn type="button" variant="secondary" onClick={() => setSigning(null)} disabled={!!busy}>Cancel</Btn><Btn type="submit" disabled={!!busy || !accepted}><PenLine size={16} /> Sign</Btn></SummitFormActions>
      </form>
    </SummitFormModal>}
  </section>;
}
